import { useForm } from '@refinedev/react-hook-form';
import type { HttpError } from '@refinedev/core';
import { useNavigate } from 'react-router';
import type { TfPromptRow, UpdatePromptBody } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface FormValues {
  name: string;
  description: string;
}

const fromPrompt = (p: Partial<TfPromptRow>): FormValues => ({
  name: p.name ?? '',
  description: p.description ?? '',
});

const toBody = (v: FormValues): UpdatePromptBody => {
  const body: UpdatePromptBody = {};
  if (v.name.trim()) body.name = v.name.trim();
  if (v.description.trim()) body.description = v.description.trim();
  return body;
};

export function PromptsEdit() {
  const navigate = useNavigate();
  const {
    refineCore: { onFinish, query },
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<TfPromptRow, HttpError, FormValues>({
    refineCoreProps: { onMutationSuccess: () => navigate('/admin/prompts') },
  });

  const initial = query?.data?.data ? fromPrompt(query.data.data) : { name: '', description: '' };

  const submit = handleSubmit(async (values) => {
    await onFinish(toBody(values) as unknown as FormValues);
  });

  if (query?.isLoading) return <div>Loading…</div>;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Edit prompt</h1>
      <p className="text-sm text-muted-foreground">
        Edit name and description here. To change content, add a new version on the prompt's detail page.
      </p>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" defaultValue={initial.name} {...register('name')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input id="description" defaultValue={initial.description} {...register('description')} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Save'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/admin/prompts')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
